import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// SSE live channel (03 Live updates): asset rows/panes update in place, jobs tray progress,
// notification badge. Offline banner + automatic retry (EventSource reconnects itself).
export function useLive() {
  const qc = useQueryClient();
  const [online, setOnline] = useState(true);
  useEffect(() => {
    let es: EventSource | null = null;
    let closed = false;
    const connect = () => {
      es = new EventSource('/v1/live', { withCredentials: true });
      es.onopen = () => setOnline(true);
      es.onerror = () => {
        setOnline(navigator.onLine && es?.readyState === EventSource.CONNECTING ? true : false);
      };
      es.onmessage = (m) => {
        let msg: { type: string; id?: string; [k: string]: unknown };
        try {
          msg = JSON.parse(m.data);
        } catch {
          return;
        }
        if (msg.type === 'asset' && msg.id) {
          window.dispatchEvent(new CustomEvent('an:asset', { detail: msg }));
          qc.setQueriesData({ queryKey: ['assets'] }, (old: any) => {
            if (!old?.items) return old;
            return { ...old, items: old.items.map((a: any) => (a.id === msg.id ? { ...a, ...(msg.connection ? { iotState: msg.connection } : {}), ...(msg.lastContactedAt ? { lastContactedAt: msg.lastContactedAt } : {}), ...(msg.position ? { position: msg.position } : {}), ...(msg.kpis ? { kpis: msg.kpis } : {}), _flash: Date.now() } : a)) };
          });
          qc.setQueryData(['asset', msg.id], (old: any) => (old ? { ...old, ...(msg.connection ? { iotState: msg.connection } : {}), ...(msg.lastContactedAt ? { lastContactedAt: msg.lastContactedAt } : {}), ...(msg.position ? { position: msg.position } : {}), ...(msg.kpis ? { kpis: msg.kpis } : {}), ...(msg.values ? { values: msg.values } : {}) } : old));
        } else if (msg.type === 'job') {
          qc.invalidateQueries({ queryKey: ['jobs'] });
        } else if (msg.type === 'notification') {
          qc.invalidateQueries({ queryKey: ['notifications'] });
        }
      };
    };
    connect();
    const on = () => { setOnline(true); navigator.serviceWorker?.controller?.postMessage('flush-outbox'); };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      closed = true;
      void closed;
      es?.close();
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [qc]);
  return online;
}
